import { useEffect, useState, useCallback, useRef } from "react";
import {
  Megaphone, Tag, DollarSign, Gift, Star, Wallet, Sparkles, Loader2, Search,
  UploadCloud, Trash2, CheckCircle2, Circle, Ticket, TrendingUp, Percent, FlaskConical,
} from "lucide-react";
import { adminApi } from "./api";
import { PageHeader } from "./shared";
import { money } from "./userCells";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";

const BLUE = "#2340b5";
const PLACEMENTS = [
  { value: "auth", label: "Login Screen" },
  { value: "my_trip", label: "My Trip" },
  { value: "community", label: "Community" },
];
const placementLabel = (v) => PLACEMENTS.find((p) => p.value === v)?.label || v || "—";
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit" }) : "");
const timeAgo = (s) => {
  if (!s) return "";
  const diff = (Date.now() - new Date(s).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};
const genCode = () => "SAFE" + Math.random().toString(36).slice(2, 8).toUpperCase();

function bytesToBase64(bytes) {
  let binary = "";
  const CH = 0x8000;
  for (let i = 0; i < bytes.length; i += CH) binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CH));
  return btoa(binary);
}

/* ---------------- KPI ---------------- */
function Kpi({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="rounded-2xl border border-[#12142b]/8 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <p className="text-sm font-medium text-[#565b73]">{label}</p>
        <span className="grid h-9 w-9 place-items-center rounded-lg" style={{ backgroundColor: `${color}14` }}>
          <Icon className="h-4 w-4" style={{ color }} />
        </span>
      </div>
      <p className="mt-3 font-display text-3xl font-black text-[#12142b]">{value}</p>
      {sub && <p className="mt-1 text-xs text-[#565b73]">{sub}</p>}
    </div>
  );
}

/* ---------------- Promo Codes ---------------- */
function PromoPanel({ promos, reload, showTest, onToggleTest }) {
  const [sub, setSub] = useState("create");
  const [type, setType] = useState("points");
  const [code, setCode] = useState("");
  const [value, setValue] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!code.trim() || !value) { toast.error("Code and value are required"); return; }
    setSaving(true);
    try {
      await adminApi.post("/marketing/promos", {
        code: code.trim().toUpperCase(), reward_type: type,
        value: Number(value), max_uses: maxUses ? Number(maxUses) : null,
      });
      toast.success(`Promo code ${code.trim().toUpperCase()} created`);
      setCode(""); setValue(""); setMaxUses("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create promo code");
    } finally { setSaving(false); }
  };

  const toggle = async (p) => {
    try {
      await adminApi.patch(`/marketing/promos/${p.id}`, { active: !p.active });
      toast.success(`${p.code} ${p.active ? "paused" : "activated"}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update promo"); }
  };

  const active = promos.filter((p) => p.active);
  const list = sub === "active" ? active : promos;
  const field = "w-full rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm outline-none focus:border-[#2340b5]";

  return (
    <div className="rounded-2xl border border-[#12142b]/8 bg-white shadow-sm" data-testid="promo-panel">
      <div className="flex items-center justify-between border-b border-[#12142b]/8 p-5">
        <p className="flex items-center gap-2 font-display text-base font-bold text-[#12142b]"><Tag className="h-4 w-4 text-[#2340b5]" /> Promo Codes</p>
        <button data-testid="promo-show-test-toggle" onClick={onToggleTest}
          className={`flex items-center gap-2 rounded-xl border px-3 py-1.5 text-xs font-semibold transition-colors ${showTest ? "border-[#f59e0b] bg-[#f59e0b]/10 text-[#b45309]" : "border-[#12142b]/12 bg-white text-[#12142b] hover:bg-[#f4f6fb]"}`}>
          <FlaskConical className="h-3.5 w-3.5" /> {showTest ? "Showing test codes" : "Show test codes"}
        </button>
      </div>
      <div className="p-5">
        <div className="mb-4 flex w-fit gap-1 border-b border-[#12142b]/8">
          <button data-testid="promo-tab-create" onClick={() => setSub("create")}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${sub === "create" ? "border-[#2340b5] text-[#2340b5]" : "border-transparent text-[#565b73]"}`}>Create New</button>
          <button data-testid="promo-tab-active" onClick={() => setSub("active")}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${sub === "active" ? "border-[#2340b5] text-[#2340b5]" : "border-transparent text-[#565b73]"}`}>Active Codes</button>
        </div>

        {sub === "create" && (
          <div className="space-y-4" data-testid="promo-create-form">
            <div>
              <p className="mb-1.5 text-sm font-semibold text-[#12142b]">Promo Type</p>
              <div className="grid grid-cols-2 gap-2">
                {[["points", "Points", "Reward points", Star], ["wallet", "Wallet Credit", "Add to wallet", Wallet]].map(([v, t, d, Icon]) => (
                  <button key={v} data-testid={`promo-type-${v}`} onClick={() => setType(v)}
                    className={`flex items-center gap-2 rounded-xl border p-3 text-left transition-all ${type === v ? "border-[#2340b5] bg-[#2340b5]/5 ring-1 ring-[#2340b5]/30" : "border-[#12142b]/12 hover:bg-[#f4f6fb]"}`}>
                    <Icon className={`h-4 w-4 ${type === v ? "text-[#2340b5]" : "text-[#565b73]"}`} />
                    <div><p className="text-sm font-semibold text-[#12142b]">{t}</p><p className="text-[11px] text-[#565b73]">{d}</p></div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-[#12142b]">Code</label>
              <div className="flex gap-2">
                <input data-testid="promo-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. WELCOME10" className={field} />
                <button data-testid="promo-generate" onClick={() => setCode(genCode())} className="shrink-0 rounded-xl border border-[#2340b5]/25 px-3 text-sm font-semibold text-[#2340b5] hover:bg-[#2340b5]/8">Generate</button>
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-[#12142b]">{type === "wallet" ? "Credit Value ($)" : "Points Value"}</label>
              <input data-testid="promo-value-input" type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder={type === "wallet" ? "e.g. 5" : "e.g. 200"} className={field} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-[#12142b]">Usage Limit <span className="font-normal text-[#565b73]">(optional)</span></label>
              <input data-testid="promo-maxuses-input" type="number" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="e.g. 100" className={field} />
              <p className="mt-1 text-xs text-[#565b73]">Leave empty for unlimited</p>
            </div>
            <button data-testid="promo-create-btn" disabled={saving} onClick={create}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#2340b5] py-3 text-sm font-bold text-white transition-colors hover:bg-[#1b3390] disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tag className="h-4 w-4" />} Create Promo Code
            </button>
          </div>
        )}

        <div className={sub === "create" ? "mt-6 border-t border-[#12142b]/8 pt-4" : ""}>
          {sub === "create" && <p className="mb-3 text-sm font-bold text-[#12142b]">Active Promo Codes</p>}
          <div className="space-y-2" data-testid="promo-list">
            {list.length === 0 && <p className="rounded-xl bg-[#f4f6fb] py-6 text-center text-sm text-[#565b73]">No promo codes yet.</p>}
            {list.map((p) => (
              <div key={p.id} data-testid={`promo-item-${p.id}`} className="flex items-center justify-between gap-2 rounded-xl border border-[#12142b]/8 p-3">
                <div>
                  <p className="font-display text-sm font-bold text-[#12142b]">{p.code}</p>
                  <p className="text-xs text-[#565b73]">{p.reward_type === "wallet" ? money(p.value) : `${p.value} pts`} · {p.uses}/{p.max_uses ?? "∞"} used</p>
                </div>
                <button data-testid={`promo-toggle-${p.id}`} onClick={() => toggle(p)}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${p.active ? "bg-[#16a34a]/12 text-[#16a34a]" : "bg-[#565b73]/10 text-[#565b73]"}`}>
                  {p.active ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />} {p.active ? "Active" : "Paused"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Ads Manager ---------------- */
function AdThumb({ ad }) {
  const [err, setErr] = useState(false);
  const src = ad.image_url || ad.media_url;
  if (src && !err) {
    return (
      <span className="grid h-11 w-14 shrink-0 place-items-center overflow-hidden rounded-lg bg-[#2340b5]/8">
        <img src={src} alt="" className="h-full w-full object-cover" onError={() => setErr(true)} />
      </span>
    );
  }
  return (
    <span className="grid h-11 w-14 shrink-0 place-items-center rounded-lg bg-[#2340b5]/8">
      <Megaphone className="h-4 w-4 text-[#2340b5]" />
    </span>
  );
}

// Images over 1MB are downscaled and re-encoded to JPEG before upload so
// mobile screens never choke on huge ad media (a 2.5MB ad failed to render
// on the My Trip screen). Videos pass through untouched.
const compressAdImage = async (file) => {
  if (!file.type.startsWith("image/") || file.size <= 1024 * 1024) return file;
  const bmp = await createImageBitmap(file);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  let blob = null;
  outer:
  for (const maxW of [1920, 1400, 1000, 720]) {
    const scale = Math.min(1, maxW / bmp.width);
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    ctx.fillStyle = "#ffffff"; // flatten any transparency
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    for (const q of [0.85, 0.7, 0.55]) {
      blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", q));
      if (blob && blob.size <= 950 * 1024) break outer;
    }
  }
  bmp.close();
  if (!blob || blob.size >= file.size) return file;
  const name = file.name.replace(/\.[a-z0-9]+$/i, "") + ".jpg";
  return new File([blob], name, { type: "image/jpeg" });
};

function AdsPanel({ ads, reload }) {
  const [placement, setPlacement] = useState("auth");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [ctaText, setCtaText] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [sponsored, setSponsored] = useState(true);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const uploadMedia = async (f) => {
    // The live API serves upload sessions from per-replica memory, so start/chunk/
    // finish can land on different replicas and fail. Retry the whole flow to recover.
    const CHUNK = 512 * 1024;
    let lastErr;
    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        setProgress(0);
        const start = await adminApi.post("/marketing/ads/upload/start", { filename: f.name, content_type: f.type });
        const uploadId = start.data.upload_id || start.data.id;
        for (let pos = 0; pos < f.size; pos += CHUNK) {
          const slice = f.slice(pos, Math.min(pos + CHUNK, f.size));
          const buf = new Uint8Array(await slice.arrayBuffer());
          await adminApi.post("/marketing/ads/upload/chunk", { upload_id: uploadId, chunk: bytesToBase64(buf) });
          setProgress(Math.min(99, Math.round(((pos + CHUNK) / f.size) * 100)));
        }
        const fin = await adminApi.post("/marketing/ads/upload/finish", { upload_id: uploadId });
        setProgress(100);
        return {
          media_url: fin.data.media_url || fin.data.url || "",
          media_type: fin.data.media_type || (f.type.startsWith("video") ? "video" : "image"),
        };
      } catch (e) {
        lastErr = e;
      }
    }
    throw lastErr;
  };

  const create = async () => {
    setSaving(true);
    try {
      let media = { media_url: "", media_type: "" };
      if (file) {
        const compressed = await compressAdImage(file);
        if (compressed !== file) {
          toast.info(`Image compressed ${(file.size / 1e6).toFixed(1)}MB → ${(compressed.size / 1e6).toFixed(2)}MB so it loads instantly in the app`);
        }
        media = await uploadMedia(compressed);
      }
      await adminApi.post("/marketing/ads", {
        placement, title: title.trim(), subtitle: subtitle.trim(),
        cta_text: ctaText.trim(), cta_url: ctaUrl.trim(),
        media_url: media.media_url, media_type: media.media_type,
        sponsored, active: true,
      });
      toast.success("Ad created");
      setTitle(""); setSubtitle(""); setCtaText(""); setCtaUrl(""); setFile(null); setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create ad");
    } finally { setSaving(false); }
  };

  const toggle = async (a) => {
    try { await adminApi.patch(`/marketing/ads/${a.id}`, { active: !a.active }); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update ad"); }
  };
  const remove = async (a) => {
    try { await adminApi.delete(`/marketing/ads/${a.id}`); toast.success("Ad deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete ad"); }
  };

  const field = "w-full rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm outline-none focus:border-[#2340b5]";

  return (
    <div className="rounded-2xl border border-[#12142b]/8 bg-white shadow-sm" data-testid="ads-panel">
      <div className="flex items-center justify-between border-b border-[#12142b]/8 p-5">
        <p className="flex items-center gap-2 font-display text-base font-bold text-[#12142b]"><Megaphone className="h-4 w-4 text-[#2340b5]" /> Ads Manager</p>
      </div>
      <div className="space-y-4 p-5" data-testid="ad-create-form">
        <div>
          <p className="text-sm font-bold text-[#12142b]">Create New Ad</p>
          <p className="text-xs text-[#565b73]">Ads will be shown in the app at the selected placement.</p>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-[#12142b]">Placement</label>
          <select data-testid="ad-placement" value={placement} onChange={(e) => setPlacement(e.target.value)} className={field}>
            {PLACEMENTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-[#12142b]">Title <span className="font-normal text-[#565b73]">(optional)</span></label>
          <input data-testid="ad-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Drive together. Save more." className={field} />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-[#12142b]">Subtitle <span className="font-normal text-[#565b73]">(optional)</span></label>
          <input data-testid="ad-subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="e.g. Share rides. Save money." className={field} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-[#12142b]">Button Text</label>
            <input data-testid="ad-cta-text" value={ctaText} onChange={(e) => setCtaText(e.target.value)} placeholder="e.g. Learn More" className={field} />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-[#12142b]">Button URL</label>
            <input data-testid="ad-cta-url" value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} placeholder="https://example.com" className={field} />
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-[#12142b]">Upload Image / Video <span className="font-normal text-[#565b73]">(optional)</span></label>
          <button type="button" data-testid="ad-upload-trigger" onClick={() => fileRef.current?.click()}
            className="flex w-full flex-col items-center gap-1 rounded-xl border-2 border-dashed border-[#12142b]/15 bg-[#f4f6fb] py-6 text-center transition-colors hover:border-[#2340b5]/40">
            <UploadCloud className="h-6 w-6 text-[#2340b5]" />
            <span className="text-sm font-semibold text-[#12142b]">{file ? file.name : "Click to upload"}</span>
            <span className="text-[11px] text-[#565b73]">Max 60MB · images over 1MB are compressed automatically</span>
          </button>
          <input ref={fileRef} data-testid="ad-file-input" type="file" accept="image/*,video/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f && f.size > 60 * 1024 * 1024) { toast.error("File exceeds 60MB"); return; } setFile(f || null); setProgress(0); }} />
          {saving && file && (
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#f4f6fb]"><div className="h-full rounded-full bg-[#2340b5] transition-all" style={{ width: `${progress}%` }} /></div>
          )}
        </div>
        <div className="flex items-center justify-between rounded-xl bg-[#f4f6fb] p-3">
          <div><p className="text-sm font-semibold text-[#12142b]">Show {"\u201C"}Sponsored{"\u201D"} label</p><p className="text-[11px] text-[#565b73]">Display a small sponsored tag on the ad</p></div>
          <Switch data-testid="ad-sponsored" checked={sponsored} onCheckedChange={setSponsored} />
        </div>
        <button data-testid="ad-create-btn" disabled={saving} onClick={create}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#2340b5] py-3 text-sm font-bold text-white transition-colors hover:bg-[#1b3390] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />} {saving ? (file ? `Uploading ${progress}%` : "Creating…") : "Create Ad"}
        </button>

        <div className="border-t border-[#12142b]/8 pt-4">
          <p className="mb-3 text-sm font-bold text-[#12142b]">Active Ads</p>
          <div className="space-y-2" data-testid="ad-list">
            {ads.length === 0 && <p className="rounded-xl bg-[#f4f6fb] py-6 text-center text-sm text-[#565b73]">No ads yet.</p>}
            {ads.map((a) => (
              <div key={a.id} data-testid={`ad-item-${a.id}`} className="flex items-center gap-3 rounded-xl border border-[#12142b]/8 p-2.5">
                <AdThumb ad={a} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[#12142b]">{a.title || "(untitled)"}</p>
                  <p className="truncate text-xs text-[#565b73]">{placementLabel(a.placement)} · {fmtDate(a.created_at)}</p>
                </div>
                <button data-testid={`ad-toggle-${a.id}`} onClick={() => toggle(a)}
                  className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${a.active ? "bg-[#16a34a]/12 text-[#16a34a]" : "bg-[#565b73]/10 text-[#565b73]"}`}>{a.active ? "Active" : "Paused"}</button>
                <button data-testid={`ad-delete-${a.id}`} onClick={() => remove(a)} className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[#e4002b] hover:bg-[#e4002b]/8"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Reward a Loyal User ---------------- */
function RewardPanel() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [points, setPoints] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (picked || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      adminApi.get("/users", { params: { search: q.trim(), limit: 6 } })
        .then((r) => setResults(r.data.items || [])).catch(() => setResults([]));
    }, 350);
    return () => clearTimeout(t);
  }, [q, picked]);

  const award = async () => {
    if (!picked || !points) { toast.error("Pick a user and points"); return; }
    setSaving(true);
    try {
      await adminApi.post("/marketing/reward", { user_id: picked.id, points: Number(points), note });
      toast.success(`${points} pts awarded to ${picked.name}`);
      setPicked(null); setQ(""); setPoints(""); setNote("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not award points"); }
    finally { setSaving(false); }
  };

  const field = "w-full rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm outline-none focus:border-[#2340b5]";
  return (
    <div className="rounded-2xl border border-[#12142b]/8 bg-white p-5 shadow-sm" data-testid="reward-panel">
      <p className="mb-4 flex items-center gap-2 font-display text-base font-bold text-[#12142b]"><Gift className="h-4 w-4 text-[#2340b5]" /> Reward a Loyal User</p>
      <div className="space-y-4">
        <div className="relative">
          <label className="mb-1.5 block text-sm font-semibold text-[#12142b]">Search user</label>
          {picked ? (
            <div className="flex items-center justify-between rounded-xl border border-[#2340b5]/25 bg-[#2340b5]/5 px-3 py-2.5">
              <div><p className="text-sm font-semibold text-[#12142b]">{picked.name}</p><p className="text-xs text-[#565b73]">{picked.phone}</p></div>
              <button data-testid="reward-clear-user" onClick={() => { setPicked(null); setQ(""); }} className="text-xs font-semibold text-[#2340b5]">Change</button>
            </div>
          ) : (
            <>
              <Search className="pointer-events-none absolute left-3 top-[38px] h-4 w-4 text-[#565b73]" />
              <input data-testid="reward-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or phone number" className={`${field} pl-9`} />
              {results.length > 0 && (
                <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-[#12142b]/12 bg-white shadow-lg" data-testid="reward-results">
                  {results.map((u) => (
                    <button key={u.id} data-testid={`reward-pick-${u.id}`} onClick={() => { setPicked(u); setResults([]); }}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-[#f4f6fb]">
                      <span className="font-medium text-[#12142b]">{u.name}</span><span className="text-xs text-[#565b73]">{u.phone}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-[#12142b]">Points to award</label>
          <input data-testid="reward-points" type="number" value={points} onChange={(e) => setPoints(e.target.value)} placeholder="e.g. 250" className={field} />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-[#12142b]">Note <span className="font-normal text-[#565b73]">(optional)</span></label>
          <textarea data-testid="reward-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. Thank you for being a loyal user!" className={`${field} resize-none`} />
        </div>
        <button data-testid="reward-award-btn" disabled={saving} onClick={award}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#2340b5] py-3 text-sm font-bold text-white transition-colors hover:bg-[#1b3390] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Award Points
        </button>
      </div>
    </div>
  );
}

/* ---------------- Recent Activity ---------------- */
const ACTIVITY_ICON = (action) => {
  if (action.startsWith("promo")) return { Icon: Tag, color: "#2340b5" };
  if (action.startsWith("ad")) return { Icon: Megaphone, color: "#7c3aed" };
  if (action.includes("point") || action.includes("reward") || action.includes("credit")) return { Icon: Gift, color: "#16a34a" };
  return { Icon: Sparkles, color: "#565b73" };
};
function ActivityPanel({ items }) {
  return (
    <div className="rounded-2xl border border-[#12142b]/8 bg-white p-5 shadow-sm" data-testid="activity-panel">
      <p className="mb-4 flex items-center gap-2 font-display text-base font-bold text-[#12142b]"><TrendingUp className="h-4 w-4 text-[#2340b5]" /> Recent Activity</p>
      <div className="space-y-3" data-testid="activity-list">
        {items.length === 0 && <p className="rounded-xl bg-[#f4f6fb] py-6 text-center text-sm text-[#565b73]">No recent marketing activity.</p>}
        {items.map((a) => {
          const { Icon, color } = ACTIVITY_ICON((a.action || "").toLowerCase());
          return (
            <div key={a.id} className="flex items-start gap-3" data-testid={`activity-${a.id}`}>
              <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg" style={{ backgroundColor: `${color}14` }}><Icon className="h-4 w-4" style={{ color }} /></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#12142b]">{a.detail || a.action}</p>
                <p className="text-xs text-[#565b73]">{a.actor_name} · {timeAgo(a.created_at)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Campaigns tab ---------------- */
function Campaigns({ promos, ads }) {
  const rows = [
    ...promos.map((p) => ({ id: p.id, kind: "Promo", name: p.code, detail: p.reward_type === "wallet" ? money(p.value) : `${p.value} pts`, usage: `${p.uses}/${p.max_uses ?? "∞"}`, active: p.active, created_at: p.created_at })),
    ...ads.map((a) => ({ id: a.id, kind: "Ad", name: a.title || "(untitled)", detail: placementLabel(a.placement), usage: "—", active: a.active, created_at: a.created_at })),
  ].sort((x, y) => new Date(y.created_at) - new Date(x.created_at));
  return (
    <div className="rounded-2xl border border-[#12142b]/8 bg-white shadow-sm" data-testid="campaigns-table">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-[#12142b]/8 text-xs uppercase tracking-wide text-[#565b73]">
            <th className="p-4 font-semibold">Type</th><th className="p-4 font-semibold">Name</th>
            <th className="p-4 font-semibold">Detail</th><th className="p-4 font-semibold">Usage</th>
            <th className="p-4 font-semibold">Status</th><th className="p-4 font-semibold">Created</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-[#565b73]">No campaigns yet.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} data-testid={`campaign-row-${r.id}`} className="border-b border-[#12142b]/5 last:border-0">
              <td className="p-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${r.kind === "Promo" ? "bg-[#2340b5]/10 text-[#2340b5]" : "bg-[#7c3aed]/10 text-[#7c3aed]"}`}>{r.kind}</span></td>
              <td className="p-4 font-semibold text-[#12142b]">{r.name}</td>
              <td className="p-4 text-[#565b73]">{r.detail}</td>
              <td className="p-4 text-[#565b73]">{r.usage}</td>
              <td className="p-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${r.active ? "bg-[#16a34a]/12 text-[#16a34a]" : "bg-[#565b73]/10 text-[#565b73]"}`}>{r.active ? "Active" : "Paused"}</span></td>
              <td className="p-4 text-[#565b73]">{fmtDate(r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function Marketing() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [promos, setPromos] = useState([]);
  const [showTestPromos, setShowTestPromos] = useState(false);
  const [ads, setAds] = useState([]);
  const [activity, setActivity] = useState([]);

  const reloadPromos = useCallback((includeTest) => {
    const inc = includeTest ?? showTestPromos;
    adminApi.get("/marketing/promos", { params: { include_test: inc } }).then((r) => setPromos(r.data.items || [])).catch(() => setPromos([]));
    adminApi.get("/marketing/overview").then((r) => setOverview(r.data)).catch(() => {});
    adminApi.get("/marketing/activity", { params: { include_test: inc } }).then((r) => setActivity(r.data.items || [])).catch(() => {});
  }, [showTestPromos]);
  const reloadAds = useCallback((includeTest) => {
    const inc = includeTest ?? showTestPromos;
    adminApi.get("/marketing/ads").then((r) => setAds(r.data.items || [])).catch(() => setAds([]));
    adminApi.get("/marketing/overview").then((r) => setOverview(r.data)).catch(() => {});
    adminApi.get("/marketing/activity", { params: { include_test: inc } }).then((r) => setActivity(r.data.items || [])).catch(() => {});
  }, [showTestPromos]);
  useEffect(() => { reloadPromos(); reloadAds(); }, [reloadPromos, reloadAds]);

  const o = overview || {};
  return (
    <div className="min-h-screen" data-testid="marketing-page">
      <PageHeader title="Promotions & Marketing" subtitle="Create promotions, manage ads and reward your loyal users." />

      <div className="space-y-5 p-8">
        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#12142b]/8" data-testid="marketing-tabs">
          {[["overview", "Overview"], ["campaigns", "Campaigns"]].map(([k, l]) => (
            <button key={k} data-testid={`mtab-${k}`} onClick={() => setTab(k)}
              className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === k ? "border-[#2340b5] text-[#2340b5]" : "border-transparent text-[#565b73] hover:text-[#12142b]"}`}>{l}</button>
          ))}
        </div>

        {tab === "overview" && (
          <>
            {/* Hero */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#2340b5] to-[#1b2f7a] p-8 text-white shadow-sm" data-testid="marketing-hero">
              <div className="relative z-10 max-w-lg">
                <h2 className="font-display text-3xl font-black leading-tight">Boost engagement.<br />Reward loyalty. Grow SafeRide.</h2>
                <p className="mt-2 text-sm text-white/80">Run promotions and ads that drive more rides and loyal users.</p>
              </div>
              <Megaphone className="absolute -right-4 top-1/2 h-44 w-44 -translate-y-1/2 rotate-12 text-white/10" strokeWidth={1.25} />
              <Percent className="absolute right-40 top-8 h-10 w-10 text-white/15" />
              <Gift className="absolute right-28 bottom-6 h-12 w-12 text-white/15" />
            </div>

            {/* KPIs (all real) */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Kpi label="Promo Codes" value={o.promos_active ?? "—"} sub="Active codes" icon={Tag} color="#2340b5" />
              <Kpi label="Ads Campaigns" value={o.ads_active ?? "—"} sub="Active ads" icon={Megaphone} color="#7c3aed" />
              <Kpi label="Promo Redemptions" value={o.redemptions ?? "—"} sub="All-time uses" icon={Ticket} color="#16a34a" />
              <Kpi label="Total Codes" value={o.promos_total ?? "—"} sub="Created all-time" icon={DollarSign} color="#d97706" />
            </div>

            {/* Promo + Ads */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <PromoPanel promos={promos} reload={reloadPromos} showTest={showTestPromos}
                onToggleTest={() => { const next = !showTestPromos; setShowTestPromos(next); reloadPromos(next); }} />
              <AdsPanel ads={ads} reload={reloadAds} />
            </div>

            {/* Reward + Activity */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <RewardPanel />
              <ActivityPanel items={activity} />
            </div>
          </>
        )}

        {tab === "campaigns" && <Campaigns promos={promos} ads={ads} />}
      </div>
    </div>
  );
}
